/* Context modules export the provider component and its hook together. */
/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { onboardingMissionForRole, onboardingStorageKey, type OnboardingStep } from "./steps";

export type OnboardingContextValue = {
  readonly active: boolean;
  readonly objectiveKey: string;
  readonly steps: readonly OnboardingStep[];
  readonly stepIndex: number;
  readonly step: OnboardingStep | null;
  readonly isFirst: boolean;
  readonly isLast: boolean;
  start: () => void;
  next: () => void;
  prev: () => void;
  finish: () => void;
};

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const permissions = usePermissions();
  const productLabel = permissions.productLabel;
  const mission = useMemo(() => onboardingMissionForRole(productLabel), [productLabel]);
  const steps = mission.steps;

  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const start = useCallback(() => {
    setStepIndex(0);
    setActive(true);
  }, []);

  const finish = useCallback(() => {
    setActive(false);
    setStepIndex(0);
    try {
      localStorage.setItem(onboardingStorageKey(productLabel), "1");
    } catch {
      /* ignore quota / private mode */
    }
  }, [productLabel]);

  const next = useCallback(() => {
    if (stepIndex >= steps.length - 1) {
      finish();
      return;
    }
    setStepIndex((i) => i + 1);
  }, [finish, stepIndex, steps.length]);

  const prev = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  // Auto-start once per role after login (AppShell pages only — user is authenticated).
  useEffect(() => {
    if (!auth.isAuthenticated || auth.isLoading) return;
    let done = false;
    try {
      done = localStorage.getItem(onboardingStorageKey(productLabel)) === "1";
    } catch {
      done = false;
    }
    if (done) return;
    const timer = window.setTimeout(() => start(), 600);
    return () => window.clearTimeout(timer);
  }, [auth.isAuthenticated, auth.isLoading, productLabel, start]);

  const value = useMemo<OnboardingContextValue>(
    () => ({
      active,
      objectiveKey: mission.objectiveKey,
      steps,
      stepIndex,
      step: active ? (steps[stepIndex] ?? null) : null,
      isFirst: stepIndex <= 0,
      isLast: stepIndex >= steps.length - 1,
      start,
      next,
      prev,
      finish,
    }),
    [active, finish, mission.objectiveKey, next, prev, start, stepIndex, steps],
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

export function useOnboarding(): OnboardingContextValue {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error("useOnboarding must be used within OnboardingProvider");
  }
  return ctx;
}
